// LIRAa — chamadas ao motor /api/liraa (Sessao 30, bloco N3, 24/09/2026 - Walber).
// Unico arquivo das telas do LIRAa que fala com a API. Campos com os NOMES EXATOS
// de app/routers/liraa.py. Modulo ISOLADO: nada aqui chama outra rota do TOVIS.
// Cores fixas claras (o TOVIS nao tem modo escuro). Nunca o laranja proibido.
import { useCallback, useEffect, useState } from 'react';
import type React from 'react';
import axios from 'axios';
import { API_URL } from './config';

const BASE = `${API_URL}/api/liraa`;

// ── Tipos (espelho das respostas do motor) ────────────────────────────────
export interface Explicacao { titulo?: string; porque?: string; causas?: string; corrigir?: string; }
export interface AvisoTubito { regra: string; mensagem: string; explicacao: Explicacao; }
export interface AreaOpcao { id: number; nome: string; rotulo_sesab: string; }
export interface AgenteOpcao { id: number; nome: string; }
export interface Opcoes { areas: AreaOpcao[]; agentes: AgenteOpcao[]; recipientes: string[]; tipos_imovel: string[]; }
export interface Ciclo {
  id: number; nome: string; data_inicio: string; data_fim: string | null; em_andamento: boolean;
  tubitos: number; pendentes: number; areas_programadas: number;
}
export interface CicloIn { nome?: string; data_inicio?: string; data_fim?: string | null; }
export interface Programacao {
  area_id: number; area_nome: string; rotulo_sesab: string; id: number | null;
  imoveis_programados: number | null; quarteiroes_sorteados: string | null; imoveis_inspecionados: number | null;
  // bloco N7: total de imoveis dos quarteiroes sorteados (um numero); programados = metade, calculados pelo motor
  imoveis_quarteiroes_sorteados: number | null;
}
export interface ProgramacaoIn {
  quarteiroes_sorteados?: string | null; imoveis_inspecionados?: number | null;
  imoveis_quarteiroes_sorteados?: number | null;
}
export interface Equipe {
  id: number; liraa_ciclo_id: number; area_id: number; area_nome: string | null;
  agente_id: number; agente_nome: string | null; quarteiroes: string | null;
  // bloco N9: imoveis dos quarteiroes do agente (digitado) e programados = metade (calculado pelo motor)
  imoveis_agente: number | null; programados_agente: number | null;
}
export interface EquipeIn { area_id?: number; agente_id?: number; quarteiroes?: string | null; imoveis_agente?: number | null; }
// bloco N9: resumo da programacao (so avisos; nao entra na auditoria nem no consolidado)
export interface ResumoAgenteArea { agente_id: number; nome: string; quarteiroes: string | null; imoveis: number | null; programados: number | null; }
export interface ResumoArea {
  area_id: number; area: string; rotulo_sesab: string; sorteados: string | null; n_sorteados: number;
  imoveis: number | null; programados: number | null; inspecionados: number | null; agentes: ResumoAgenteArea[];
  soma_imoveis_agentes: number | null; soma_programados_agentes: number | null; quarteiroes_sem_agente: string[]; avisos: string[];
}
export interface ResumoAgente {
  agente_id: number; nome: string; incompleto: boolean; total_imoveis: number | null; total_programados: number | null;
  areas: { area_id: number; area: string; rotulo_sesab: string; quarteiroes: string | null; imoveis: number | null; programados: number | null }[];
}
export interface ResumoProgramacao {
  ciclo: { id: number; nome: string; data_inicio: string; data_fim: string | null };
  por_area: ResumoArea[]; por_agente: ResumoAgente[];
  totais: { areas: number; quarteiroes_sorteados: number; imoveis: number | null; programados: number | null;
    soma_programados_agentes: number | null; inspecionados: number | null };
  avisos: string[]; gerado_em: string;
}
export const CAMPOS_LAB = ['lab_aeg_larvas', 'lab_aeg_pupas', 'lab_alb_larvas', 'lab_alb_pupas', 'lab_outros_larvas', 'lab_outros_pupas'] as const;
export type CampoLab = typeof CAMPOS_LAB[number];
export interface Tubito {
  id: number; liraa_ciclo_id: number; data: string; agente_id: number; area_id: number;
  quarteirao: string | null; logradouro: string | null; numero: string | null;
  tipo_imovel: string; recipiente: string; deposito_descricao: string | null; numero_amostra: number;
  larvas_campo: number | null; pupas_campo: number | null; observacao: string | null;
  lab_aeg_larvas: number | null; lab_aeg_pupas: number | null; lab_alb_larvas: number | null;
  lab_alb_pupas: number | null; lab_outros_larvas: number | null; lab_outros_pupas: number | null;
  lab_analisado: boolean; lab_data: string | null; lab_responsavel: string | null;
  agente_nome: string | null; area_nome: string | null; positivo_aeg: boolean; positivo_alb: boolean;
  avisos?: AvisoTubito[];
}
export interface TubitoIn {
  data?: string; agente_id?: number; area_id?: number; quarteirao?: string | null; logradouro?: string | null;
  numero?: string | null; tipo_imovel?: string; recipiente?: string; deposito_descricao?: string | null;
  numero_amostra?: number; larvas_campo?: number | null; pupas_campo?: number | null; observacao?: string | null;
  com_resultado?: boolean;
  lab_aeg_larvas?: number | null; lab_aeg_pupas?: number | null; lab_alb_larvas?: number | null;
  lab_alb_pupas?: number | null; lab_outros_larvas?: number | null; lab_outros_pupas?: number | null;
  lab_data?: string | null; lab_responsavel?: string | null;
}
export type ResultadoIn = Partial<Record<CampoLab, number | null>> & { lab_data?: string | null; lab_responsavel?: string | null };
export const RECIPIENTES = ['A1', 'A2', 'B', 'C', 'D1', 'D2', 'E'] as const;
type PorRecipiente = Record<typeof RECIPIENTES[number], number>;
export interface Contagem {
  tubitos: { total: number; pendentes: number; analisados: number };
  imoveis_com_tubito: number;
  imoveis_aeg: { TB: number; Outro: number; total: number };
  imoveis_alb: { TB: number; Outro: number; total: number };
  rec_aeg: PorRecipiente; rec_alb: PorRecipiente; rec_aeg_total: number; rec_alb_total: number;
  imoveis_programados: number | null; imoveis_inspecionados: number | null;
  iip_aeg: number | null; ib_aeg: number | null; iip_alb: number | null; ib_alb: number | null;
  classificacao: 'satisfatório' | 'alerta' | 'risco' | null;
}
export interface QuadroArea extends Contagem { area_id: number; area_nome: string | null; rotulo_sesab: string; }
export interface QuadroAgente {
  agente_id: number; agente_nome: string | null; tubitos: number; pendentes: number;
  tubitos_positivos_aeg: number; tubitos_positivos_alb: number; imoveis_positivos_aeg: number; imoveis_positivos_alb: number;
}
export interface Quadro { ciclo: Ciclo; areas: QuadroArea[]; total: Contagem; por_agente: QuadroAgente[]; }
export interface ItemAuditoria {
  regra: string; nivel: 'trava' | 'aviso'; area_id: number | null; area_nome: string | null;
  tubito_ids: number[]; mensagem: string; explicacao: Explicacao;
}
export interface Auditoria { auditado_em: string; travas: number; avisos: number; itens: ItemAuditoria[]; }
export interface LinhaConsolidado {
  bairro: string; imoveis_programados: number | null; imoveis_inspecionados: number | null;
  aeg_tb: number; aeg_outros: number; alb_tb: number; alb_outros: number;
  iip_aeg: number | null; ib_aeg: number | null; iip_alb: number | null; ib_alb: number | null;
  rec_aeg_A1: number; rec_aeg_A2: number; rec_aeg_B: number; rec_aeg_C: number;
  rec_aeg_D1: number; rec_aeg_D2: number; rec_aeg_E: number; rec_alb: number;
}
export interface Consolidado {
  pronto: boolean; pendencias: string[]; linhas: LinhaConsolidado[] | null;
  rodape: { total_bairros: number; total_programados: number | null; total_inspecionados: number } | null;
  auditado_em: string;
}

// ── Erros: o "detail" do motor vira texto em portugues ────────────────────
export function mensagemErro(e: any, padrao = 'Não foi possível falar com o sistema.'): string {
  const st = e?.response?.status;
  const d = e?.response?.data?.detail;
  if (st === 401) return 'Sua sessão expirou. Entre de novo no TOVIS.';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map((x: any) => {
      const campo = Array.isArray(x?.loc) ? x.loc[x.loc.length - 1] : '';
      return campo ? `Campo "${campo}": valor inválido.` : (x?.msg || 'Valor inválido.');
    }).join('\n');
  }
  if (!e?.response) return 'Sem resposta do sistema. Confira a internet e tente de novo.';
  return padrao;
}
export const statusErro = (e: any): number | undefined => e?.response?.status;

// ── Chamadas ──────────────────────────────────────────────────────────────
const h = (token: string | null) => ({ headers: { Authorization: `Bearer ${token}` } });

export const liraaApi = {
  opcoes: (t: string | null) => axios.get<Opcoes>(`${BASE}/opcoes`, h(t)).then(r => r.data),

  ciclos: (t: string | null) => axios.get<Ciclo[]>(`${BASE}/ciclos`, h(t)).then(r => r.data),
  criarCiclo: (t: string | null, c: CicloIn) => axios.post<Ciclo>(`${BASE}/ciclos`, c, h(t)).then(r => r.data),
  editarCiclo: (t: string | null, id: number, c: CicloIn) => axios.put<Ciclo>(`${BASE}/ciclos/${id}`, c, h(t)).then(r => r.data),
  excluirCiclo: (t: string | null, id: number) => axios.delete(`${BASE}/ciclos/${id}`, h(t)).then(r => r.data),

  programacao: (t: string | null, cid: number) => axios.get<Programacao[]>(`${BASE}/ciclos/${cid}/programacao`, h(t)).then(r => r.data),
  salvarProgramacao: (t: string | null, cid: number, areaId: number, p: ProgramacaoIn) =>
    axios.put<Programacao>(`${BASE}/ciclos/${cid}/programacao/${areaId}`, p, h(t)).then(r => r.data),

  equipe: (t: string | null, cid: number) => axios.get<Equipe[]>(`${BASE}/ciclos/${cid}/equipe`, h(t)).then(r => r.data),
  criarEquipe: (t: string | null, cid: number, e: EquipeIn) => axios.post<Equipe>(`${BASE}/ciclos/${cid}/equipe`, e, h(t)).then(r => r.data),
  editarEquipe: (t: string | null, id: number, e: EquipeIn) => axios.put<Equipe>(`${BASE}/equipe/${id}`, e, h(t)).then(r => r.data),
  excluirEquipe: (t: string | null, id: number) => axios.delete(`${BASE}/equipe/${id}`, h(t)).then(r => r.data),
  resumoProgramacao: (t: string | null, cid: number) =>
    axios.get<ResumoProgramacao>(`${BASE}/ciclos/${cid}/resumo-programacao`, h(t)).then(r => r.data),

  tubitos: (t: string | null, cid: number, f: { area_id?: number; agente_id?: number; status?: 'pendente' | 'analisado' } = {}) =>
    axios.get<Tubito[]>(`${BASE}/ciclos/${cid}/tubitos`, { ...h(t), params: f }).then(r => r.data),
  proximoNumero: (t: string | null, cid: number, agenteId: number) =>
    axios.get<{ agente_id: number; proximo: number }>(`${BASE}/ciclos/${cid}/proximo-numero`, { ...h(t), params: { agente_id: agenteId } }).then(r => r.data.proximo),
  criarTubito: (t: string | null, cid: number, b: TubitoIn) => axios.post<Tubito>(`${BASE}/ciclos/${cid}/tubitos`, b, h(t)).then(r => r.data),
  editarTubito: (t: string | null, id: number, b: TubitoIn) => axios.put<Tubito>(`${BASE}/tubitos/${id}`, b, h(t)).then(r => r.data),
  lancarResultado: (t: string | null, id: number, b: ResultadoIn) => axios.put<Tubito>(`${BASE}/tubitos/${id}/resultado`, b, h(t)).then(r => r.data),
  desfazerResultado: (t: string | null, id: number) => axios.delete<Tubito>(`${BASE}/tubitos/${id}/resultado`, h(t)).then(r => r.data),
  excluirTubito: (t: string | null, id: number) => axios.delete(`${BASE}/tubitos/${id}`, h(t)).then(r => r.data),

  quadro: (t: string | null, cid: number, areaId?: number) =>
    axios.get<Quadro>(`${BASE}/ciclos/${cid}/quadro`, { ...h(t), params: areaId ? { area_id: areaId } : {} }).then(r => r.data),
  auditoria: (t: string | null, cid: number) => axios.get<Auditoria>(`${BASE}/ciclos/${cid}/auditoria`, h(t)).then(r => r.data),
  consolidado: (t: string | null, cid: number) => axios.get<Consolidado>(`${BASE}/ciclos/${cid}/consolidado`, h(t)).then(r => r.data),
};

// ── Armazenamento de sessao (sempre em try/catch) ─────────────────────────
export const CHAVE_CICLO = 'tovis-liraa-ciclo';
export function lerSessao(k: string): string | null {
  try { return sessionStorage.getItem(k); } catch { return null; }
}
export function gravarSessao(k: string, v: string | null) {
  try { if (v === null) sessionStorage.removeItem(k); else sessionStorage.setItem(k, v); } catch { /* sem armazenamento */ }
}

/** Ciclo escolhido, compartilhado pelos 3 cartoes. Sem escolha salva: o mais recente. */
export function useCiclosLiraa(token: string | null) {
  const [ciclos, setCiclos] = useState<Ciclo[] | null>(null);
  const [erro, setErro] = useState('');
  const [cicloId, setCicloIdState] = useState<number | null>(() => {
    const v = Number(lerSessao(CHAVE_CICLO));
    return Number.isFinite(v) && v > 0 ? v : null;
  });
  const setCicloId = useCallback((id: number | null) => {
    setCicloIdState(id);
    gravarSessao(CHAVE_CICLO, id ? String(id) : null);
  }, []);
  const recarregar = useCallback(async (preferir?: number) => {
    try {
      const lista = await liraaApi.ciclos(token);
      setCiclos(lista);
      setErro('');
      setCicloIdState(atual => {
        const escolhido = [preferir, atual].find(x => x && lista.some(c => c.id === x)) ?? lista[0]?.id ?? null;
        gravarSessao(CHAVE_CICLO, escolhido ? String(escolhido) : null);
        return escolhido;
      });
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível carregar os ciclos do LIRAa.'));
    }
  }, [token]);
  useEffect(() => { recarregar(); }, [recarregar]);
  const ciclo = ciclos?.find(c => c.id === cicloId) ?? null;
  return { ciclos, ciclo, cicloId, setCicloId, recarregar, erro };
}

// ── Formatos (virgula e dd/mm/aaaa em TODA exibicao) ──────────────────────
export function fmtData(iso?: string | null): string {
  if (!iso) return '—';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}
export function fmtIndice(v?: number | null): string {
  return v === null || v === undefined ? '—' : v.toFixed(2).replace('.', ',');
}
export function fmtNum(v?: number | null): string {
  return v === null || v === undefined ? '—' : String(v);
}
export function fmtHora(iso?: string | null): string {
  if (!iso) return '--:--';
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]} às ${m[4]}:${m[5]}` : iso;
}
export function periodo(c?: Ciclo | null): string {
  if (!c) return '—';
  return `${fmtData(c.data_inicio)} — ${c.data_fim ? fmtData(c.data_fim) : 'em andamento'}`;
}
export function hojeISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
/** '' -> null; '5' -> 5; texto invalido -> NaN (a tela avisa). */
export function paraInt(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  return /^\d+$/.test(t) ? Number(t) : NaN;
}

// ── Visual compartilhado (mesma paleta do mural do Quadro de Positividade) ─
export type Nivel = 'verde' | 'amarelo' | 'vermelho' | 'cinza';
export const COR: Record<Nivel, { forte: string; fundo: string; texto: string }> = {
  vermelho: { forte: '#C62828', fundo: '#FDECEC', texto: '#8E1B1B' },
  amarelo: { forte: '#A86B00', fundo: '#FFF6DC', texto: '#6B4400' },
  verde: { forte: '#1E7A4C', fundo: '#E6F4EC', texto: '#14553A' },
  cinza: { forte: '#5B6778', fundo: '#EEF1F5', texto: '#3C4757' },
};
export const RISCO_ESCURO = '#8E1B1B';
export const TINTA = '#122033';
export const SUB = '#4A5A70';
export const BORDA = '#D5DFEA';
export const PAGINA = '#eaf0f7';
export const ACENTO = { ciclo: '#0E7490', laboratorio: '#6D28D9', quadro: '#1D4ED8' };

export const S: Record<string, React.CSSProperties> = {
  pagina: { minHeight: '100vh', background: PAGINA, color: TINTA, paddingBottom: 40 },
  miolo: { maxWidth: 1280, margin: '0 auto', padding: '16px 20px' },
  card: { background: '#ffffff', border: `1px solid ${BORDA}`, borderRadius: 12, padding: '14px 16px', marginBottom: 14 },
  tituloSecao: { fontSize: 17, fontWeight: 800, margin: '0 0 10px', color: TINTA },
  rotulo: { display: 'block', fontSize: 13, fontWeight: 700, color: TINTA, marginBottom: 4 },
  campo: { width: '100%', boxSizing: 'border-box', padding: '8px 10px', borderRadius: 8, border: '2px solid #9fb3cc', background: '#ffffff', color: TINTA, fontSize: 15 },
  botao: { minHeight: 40, padding: '7px 14px', borderRadius: 8, border: `2px solid ${TINTA}`, background: '#ffffff', color: TINTA, fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 },
  botaoMini: { padding: '4px 10px', borderRadius: 6, border: `1.5px solid ${TINTA}`, background: '#ffffff', color: TINTA, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  th: { padding: '6px 6px', fontSize: 12, fontWeight: 800, background: '#f0f4f8', borderBottom: `2px solid ${BORDA}`, textAlign: 'center', whiteSpace: 'nowrap' },
  td: { padding: '5px 6px', fontSize: 14, borderBottom: '1px solid #e3e9f0', textAlign: 'center', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
};
export const botaoCor = (cor: string): React.CSSProperties => ({ ...S.botao, background: cor, borderColor: cor, color: '#ffffff' });
export const caixa = (n: Nivel): React.CSSProperties => ({
  background: COR[n].fundo, color: COR[n].texto, borderLeft: `6px solid ${COR[n].forte}`, borderRadius: 10,
  padding: '10px 14px', marginBottom: 12, whiteSpace: 'pre-line', fontSize: 15,
});
